import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFormContext,
} from 'react-hook-form';

import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';

import { ProductSummary } from 'src/organisms/product-purchase/ProductSummary';

import { CustomerInfoFieldset } from '../components/CustomerInfoFieldset';
import { PaymentMethodFieldset } from '../components/PaymentMethodFieldset';
import {
  ReservationFlowProductDetail,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { useGetReservationFlowMe } from 'src/hooks/api/reservation-flow/useGetReservationFlowMe';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { TokenResponse } from '@mul-pay/mptoken-js';

type InputPaymentInfoTemplateProps = {
  productDetail: ReservationFlowProductDetail;
  onNext: () => void;
  onBack: () => void;
  handlePaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
  isMulpayOrderEnabled?: boolean;
  handleMulpayTokenChange: (tokenResponse?: TokenResponse) => void;
  merchantPublicId: string;
};

export const InputPaymentInfoTemplate: FC<InputPaymentInfoTemplateProps> = ({
  productDetail,
  onNext,
  onBack,
  handlePaymentElementChange,
  isMulpayOrderEnabled,
  handleMulpayTokenChange,
  merchantPublicId,
}) => {
  const intl = useIntl();
  const {
    formState: { isValid, errors },
  } = useFormContext();

  const { data: user } = useGetReservationFlowMe();

  if (!user) return null;

  return (
    <div>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={
            productDetail.scheme === 'subscription'
              ? intl.formatMessage({
                  defaultMessage: '月謝購入',
                  id: 'X3CcBu',
                  description:
                    'src/templates/merchants/purchase/InputPaymentInfoTemplate/index.tsx',
                })
              : intl.formatMessage({
                  defaultMessage: '回数券購入',
                  id: 'WcP4Dc',
                  description:
                    'src/templates/merchants/purchase/InputPaymentInfoTemplate/index.tsx',
                })
          }
          leftButton={
            <RSDetailHeaderButton
              variant="close"
              type="button"
              onClick={onBack}
            >
              {intl.formatMessage({
                defaultMessage: '戻る',
                id: 'E+JQAH',
                description:
                  'src/templates/merchants/purchase/InputPaymentInfoTemplate/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
          rightButton={
            <RSDetailHeaderButton
              variant="ok"
              form="ticketBookPurchaseForm"
              disabled={!isValid}
            >
              {intl.formatMessage({
                defaultMessage: '次へ',
                id: 'NkzeEa',
                description:
                  'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/InputPaymentInfoTemplate/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
        />
      </div>

      <form
        id="ticketBookPurchaseForm"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onNext();
        }}
      >
        <div className="flex justify-center">
          <div className="max-w-[496px] grow">
            <div className="pc:px-0 pc:pt-4 border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-50 px-2.5 pb-2.5 pt-3">
              <ProductSummary productDetail={productDetail} />
            </div>
            <div className="pc:px-0 pc:pb-4 space-y-5 px-2.5 pb-6 pt-2.5">
              {productDetail.customer_fields.length > 0 && (
                <CustomerInfoFieldset
                  customerFields={productDetail.customer_fields}
                />
              )}
              <PaymentMethodFieldset
                user={user}
                stripeErrorMessage={
                  (
                    errors.paymentMethod as Merge<
                      FieldError,
                      FieldErrorsImpl<ReservationFlowSelectedPaymentMethodNewCreditCard>
                    >
                  )?.creditCardInfo?.message ?? undefined
                }
                isMonthlyBilling={productDetail.scheme === 'subscription'}
                handlePaymentElementChange={handlePaymentElementChange}
                isMulpayOrderEnabled={isMulpayOrderEnabled}
                handleMulpayTokenChange={handleMulpayTokenChange}
                merchantPublicId={merchantPublicId}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
